/**
 * Duplicate-detection & merge service.
 *
 * Wraps the admin-only duplicate scan, the intake-time auto-suggest, and the
 * merge endpoints for both individuals and donors.
 */
import { apiGet, apiPost } from './api';

export interface MatchedRule {
  name: string;
  label: string;
  confidence: number;
}

export interface DuplicateIndividual {
  individualID: number;
  clientID: string | null;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  raceEthnicity: string | null;
  address: string | null;
  city: string | null;
  zipCode: string | null;
  phoneNumber: string | null;
  emailAddress: string | null;
  clientType: string | null;
  isActive: boolean;
}

export interface DuplicateDonor {
  donorID: number;
  fullName: string | null;
  salutation: string | null;
  organizationCompany: string | null;
  emailAddress: string | null;
  phoneNumber: string | null;
  mailingAddress: string | null;
  birthdayMonth: number | null;
  birthdayDay: number | null;
  donorType: string | null;
  priorityLevel: string | null;
  preferredRecognition: string | null;
  isActive: boolean;
}

export interface DuplicateGroup<T> {
  confidence: number;
  matchedRules: MatchedRule[];
  members: T[];
}

export interface DuplicateScanResult<T> {
  groups: DuplicateGroup<T>[];
  totalGroups: number;
  ruleStats: Record<string, number>;
}

export interface IntakeSuggestion extends DuplicateIndividual {
  confidence: number;
  reasons: string[];
}

export interface MergeResult {
  mergeID: number;
  rowsRepointed: Record<string, number>;
  winnerIndividualID?: number;
  loserIndividualID?: number;
  winnerDonorID?: number;
  loserDonorID?: number;
}

/**
 * Admin: scan for duplicate clients.
 */
export async function scanIndividualDuplicates(
  minConfidence = 0
): Promise<DuplicateScanResult<DuplicateIndividual>> {
  const response = await apiGet<DuplicateScanResult<DuplicateIndividual>>(
    '/individuals/duplicates',
    { minConfidence }
  );
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to scan for duplicate clients');
  }
  return response.data;
}

/**
 * Admin: scan for duplicate donors.
 */
export async function scanDonorDuplicates(
  minConfidence = 0
): Promise<DuplicateScanResult<DuplicateDonor>> {
  const response = await apiGet<DuplicateScanResult<DuplicateDonor>>('/donors/duplicates', {
    minConfidence,
  });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to scan for duplicate donors');
  }
  return response.data;
}

/**
 * Intake-time auto-suggest. Any authenticated user can call this — used by the
 * client intake form to warn staff before creating a likely duplicate.
 */
export async function checkIntakeDuplicates(input: {
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  emailAddress?: string;
}): Promise<IntakeSuggestion[]> {
  if (!input.firstName?.trim() || !input.lastName?.trim()) {return [];}
  const response = await apiGet<{ suggestions: IntakeSuggestion[]; count: number }>(
    '/individuals/duplicate-check',
    input
  );
  if (!response.success) {
    return [];
  }
  return response.data?.suggestions || [];
}

/**
 * Admin: merge `loserID` into `winnerID`. fieldChoices maps camelCase field
 * names to "winner" or "loser" — fields not listed default to keeping winner.
 */
export async function mergeIndividuals(
  winnerID: number,
  loserID: number,
  fieldChoices: Record<string, 'winner' | 'loser'>
): Promise<MergeResult> {
  const response = await apiPost<MergeResult>(`/individuals/${winnerID}/merge`, {
    loserID,
    fieldChoices,
  });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to merge clients');
  }
  return response.data;
}

export async function mergeDonors(
  winnerID: number,
  loserID: number,
  fieldChoices: Record<string, 'winner' | 'loser'>
): Promise<MergeResult> {
  const response = await apiPost<MergeResult>(`/donors/${winnerID}/merge`, {
    loserID,
    fieldChoices,
  });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to merge donors');
  }
  return response.data;
}
